import { useEffect, useRef, useCallback } from 'react';
import { Video, ArticleVariant, PaginatedResponse } from '@repo/types';
import { Pagination } from '../../../siteStructure/components/Pagination';
import { VideoCard } from '../VideoCard/VideoCard';
import { VideoCardSkeleton } from '../VideoCard/VideoCardSkeleton';
import classNames from 'classnames';
import { VideoListProps } from './contracts';

export type VideoListUIProps = VideoListProps & {
  paginatedVideos: PaginatedResponse<Video>;
  allVideos?: Video[];
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
};

export default function VideoListUI({
  title,
  paginatedVideos,
  allVideos,
  withPagination,
  lazyLoadPagination = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  variant,
  showDate,
  showTag,
  columns = 3,
  prioritize = false,
}: VideoListUIProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  const videos = lazyLoadPagination && allVideos ? allVideos : paginatedVideos?.items ?? [];

  // Infinite scroll observer
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const entry = entries[0];
      if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    if (!lazyLoadPagination) return;

    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin: '100px',
      threshold: 0,
    });

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [lazyLoadPagination, handleIntersection]);

  if (videos.length === 0 && !isFetchingNextPage) {
    return null;
  }

  const className = classNames('article-card-list-container', {
    'article-card-list-container--with-pagination': withPagination && !lazyLoadPagination,
    'article-card-list-container--lazy-load': lazyLoadPagination,
    'article-card-list-container--default': variant === ArticleVariant.Default,
    'article-card-list-container--text-side-by-side': variant === ArticleVariant.TextSideBySide,
  });

  const skeletonCount = columns * 2;

  return (
    <div className={className} id="video-list">
      {title && <h3 className="article-card-list-container__title">{title}</h3>}
      <ul className="article-card-list-container__list" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {videos.map((video) => {
          return (
            <li className="article-card-list-container__list-item" key={video.id}>
              <VideoCard
                variant={variant}
                video={video}
                showDate={showDate}
                showTag={showTag}
                prioritize={prioritize}
              />
            </li>
          );
        })}
        {lazyLoadPagination &&
          isFetchingNextPage &&
          Array.from({ length: skeletonCount }).map((_, index) => (
            <li
              className="article-card-list-container__list-item article-card-list-container__list-item--skeleton"
              key={`skeleton-${index}`}
            >
              <VideoCardSkeleton variant={variant} showTag={showTag} showDate={showDate} />
            </li>
          ))}
      </ul>
      {lazyLoadPagination && <div ref={loadMoreRef} className="article-card-list-container__load-more-trigger" />}
      {withPagination && !lazyLoadPagination && (
        <Pagination
          forcePage={paginatedVideos.pagination.currentPage - 1}
          pageCount={paginatedVideos.pagination.totalPages}
        />
      )}
    </div>
  );
}
